import * as tslib_1 from "tslib";
import { ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { chain, cloneDeep, findIndex, some } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
export var searchManyInputStyles;
(function (searchManyInputStyles) {
    searchManyInputStyles["Select"] = "Select";
    searchManyInputStyles["Outlined"] = "Outlined";
    searchManyInputStyles["Underline"] = "Underline";
})(searchManyInputStyles || (searchManyInputStyles = {}));
var FcSearchManyInputComponent = /** @class */ (function () {
    function FcSearchManyInputComponent(fuseTranslationLoaderService, translate) {
        this.fuseTranslationLoaderService = fuseTranslationLoaderService;
        this.translate = translate;
        this.requiredMark = false;
        this.desiredStyle = searchManyInputStyles.Select;
        this.disabled = false;
        this.showSearchIcon = false;
        this.tooltipPosition = 'below';
        this.optionsLimit = 10;
        this.removeSubscriptions = new Subject();
        this.options = [];
        this.loading = false;
        this.displayText = '';
        this.hasMoreOptions = true;
        this.optionsOffset = 0;
        this.styleToDisplay = 'select-style';
        this.searchTerm = new FormControl(null);
        this.fuseTranslationLoaderService.loadJsonTranslations({ english: english, portuguese: portuguese, spanish: spanish });
    }
    FcSearchManyInputComponent.prototype.ngOnDestroy = function () {
        this.removeSubscriptions.next();
        this.removeSubscriptions.complete();
    };
    FcSearchManyInputComponent.prototype.ngOnInit = function () {
        var _this = this;
        var controlValue = this.control.value || [];
        this.styleToDisplay = this.getDesiredStyles();
        this.displayText = this.formatDisplayText(controlValue);
        this.options = cloneDeep(controlValue);
        this.searchTerm.valueChanges
            .pipe(takeUntil(this.removeSubscriptions), debounceTime(500), tap(function () { return _this.resetValues(); }), map(function (value) { return value || ''; }), filter(function (value) { return typeof value === 'string'; }), map(function (value) { return value.trim(); }), tap(function () { return _this.loading = true; }), switchMap(function () { return _this.search(_this.inputFilter.nativeElement.value, _this.optionsLimit, 0); }), tap(function () { return _this.loading = false; }))
            .subscribe(function (options) {
            if (options.length < _this.optionsLimit) {
                _this.hasMoreOptions = false;
            }
            _this.keepOptionChecked(options);
            _this.options = options;
        });
        this.control.valueChanges.pipe(takeUntil(this.removeSubscriptions)).subscribe(function (value) {
            value = value || [];
            _this.displayText = _this.formatDisplayText(value);
            if (!value.length) {
                _this.options.forEach(function (option) {
                    if (option.selected) {
                        option.selected = false;
                    }
                });
                _this.resetValues();
            }
        });
        this.searchTerm.reset();
    };
    FcSearchManyInputComponent.prototype.formatDisplayText = function (controlValue) {
        var archivedLabel = this.translate.instant('SEARCH_MANY_INPUT.ARCHIVED_OPTION').toLowerCase();
        var formattedDisplayText = controlValue.map(function (value) {
            if (value.archived) {
                return value.name + " (" + archivedLabel + ")";
            }
            else {
                return value.name;
            }
        }).join(', ');
        return formattedDisplayText;
    };
    FcSearchManyInputComponent.prototype.getDesiredStyles = function () {
        switch (this.desiredStyle) {
            case searchManyInputStyles.Select:
                return 'select-style';
            case searchManyInputStyles.Outlined:
                return 'outlined-style';
            case searchManyInputStyles.Underline:
                return 'underline-style';
            default:
                return 'select-style';
        }
    };
    FcSearchManyInputComponent.prototype.resetValues = function () {
        this.optionsOffset = 0;
        this.hasMoreOptions = true;
    };
    FcSearchManyInputComponent.prototype.keepOptionChecked = function (options) {
        var currentValue = this.control.value || [];
        options.forEach(function (option) {
            option.selected = some(currentValue, {
                id: option.id
            });
        });
    };
    FcSearchManyInputComponent.prototype.loadMoreOptions = function () {
        var _this = this;
        this.search(this.inputFilter.nativeElement.value, this.optionsLimit, this.optionsOffset).then((function (options) {
            var _a;
            _this.keepOptionChecked(options);
            (_a = _this.options).push.apply(_a, tslib_1.__spread(options));
            _this.loading = false;
            if (options.length < _this.optionsLimit) {
                _this.hasMoreOptions = false;
            }
        }));
    };
    FcSearchManyInputComponent.prototype.onAutocompleteScroll = function () {
        var _this = this;
        if (this.options.length) {
            this.loading = false;
        }
        setTimeout(function () {
            if (_this.matAutocomplete &&
                _this.matAutocomplete.panel &&
                _this.matAutocomplete.panel.nativeElement) {
                _this.matAutocomplete.panel.nativeElement.scrollTo({
                    top: 0,
                });
                _this.scrollHandler = function (event) {
                    if (_this.hasMoreOptions) {
                        var _a = event.target, scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, clientHeight = _a.clientHeight;
                        var marginOfError = 1;
                        if ((scrollTop + clientHeight) >= (scrollHeight - marginOfError)) {
                            _this.loading = true;
                            _this.optionsOffset += _this.optionsLimit;
                            _this.loadMoreOptions();
                        }
                    }
                };
                _this.matAutocomplete.panel.nativeElement.addEventListener('scroll', _this.scrollHandler);
            }
        });
    };
    FcSearchManyInputComponent.prototype.onOptionCleared = function () {
        this.control.setValue(null);
        this.displayTextInput.nativeElement.focus();
    };
    FcSearchManyInputComponent.prototype.onBlur = function () {
        if (this.hasMoreOptions &&
            !this.options.length) {
            this.searchTerm.setValue('');
        }
    };
    FcSearchManyInputComponent.prototype.onCheck = function (event, option) {
        event.stopPropagation();
        event.preventDefault();
        this.displayTextInput.nativeElement.click();
        var value = this.control.value || [];
        var index = findIndex(value, {
            id: option.id
        });
        var selected = index >= 0;
        if (selected) {
            value.splice(index, 1);
        }
        else {
            value.push(option);
        }
        option.selected = !selected;
        var newValue = chain(value).sortBy('name').cloneDeep().value();
        this.control.setValue(newValue.length ? newValue : null);
    };
    FcSearchManyInputComponent.prototype.onCloseAutocomplete = function () {
        this.inputFilter.nativeElement.value = '';
    };
    FcSearchManyInputComponent.prototype.onClickInputFilter = function (event) {
        event.stopPropagation();
        this.inputFilter.nativeElement.focus();
    };
    FcSearchManyInputComponent.prototype.openPanel = function (event) {
        event.stopPropagation();
        this.displayTextInput.nativeElement.focus();
        this.displayTextInput.nativeElement.click();
    };
    FcSearchManyInputComponent.prototype.isSelected = function (option) {
        var value = this.control.value || [];
        return some(value, {
            id: option.id
        });
    };
    FcSearchManyInputComponent.prototype.clearSelection = function () {
        this.control.setValue(null);
        this.searchTerm.setValue('');
        this.options.forEach(function (option) {
            option.selected = false;
        });
    };
    return FcSearchManyInputComponent;
}());
export { FcSearchManyInputComponent };
